import { CheckCircle2, XCircle, MinusCircle, ShieldAlert, Briefcase, Home, FileSearch, TrendingUp, AlertTriangle } from "lucide-react";
import { fmtDate } from "@/lib/dateFormat";

/**
 * DecisionPointReport — premium graphical view of TransUnion / NTN DecisionPoint metrics
 * for property managers. Designed in NTN's navy + warm-neutrals brand language with
 * SVG arc gauges, FICO progress bars, pill badges, and rent-to-income visualisation.
 */
export default function DecisionPointReport({ dp, monthlyRent, ordered = [] }) {
  if (!dp || (dp.dp_score == null && dp.fico_score == null && !dp.fico_status && !dp.dp_score_status
      && dp.eviction_records_found == null
      && dp.criminal_records_found == null && !dp.employment_verified
      && !dp.landlord_verified && !dp.document_validation && !dp.staff_notes)) {
    return null;
  }

  const verdict = dp.final_verdict || dp.suggested_verdict;

  return (
    <section className="bg-white border border-[#0A192F]/10 rounded-sm p-6 sm:p-8" data-testid="dp-report">
      <header className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-overline text-[#4A5568]">DecisionPoint · NTN</div>
          <h3 className="font-serif text-2xl sm:text-3xl text-[#0A192F] tracking-tight mt-1">
            Risk Assessment
          </h3>
        </div>
        {verdict && <VerdictPill verdict={verdict} overridden={!!dp.staff_verdict_override} />}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ScoreGauge label="DecisionPoint Score"
                    value={dp.dp_score_status === "not_available" ? null : dp.dp_score} max={100}
                    naLabel={dp.dp_score_status === "not_available" ? "N/A" : null} />
        <FicoBar value={dp.fico_score} status={dp.fico_status} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
        {/* v1.0.6: only render a category card if that product was ordered.
             null result → "Awaiting data" (pending), never a false clear. */}
        {ordered.includes("credit_report") && (
        <MetricCard
          icon={<ShieldAlert className="h-4 w-4" />}
          label="Eviction History"
          value={booleanLabel(dp.eviction_records_found, "No records found", "Records found")}
          tone={toneForBool(dp.eviction_records_found, /*invert*/ true)}
          detail={dp.eviction_count ? `${dp.eviction_count} record${dp.eviction_count > 1 ? "s" : ""}` : dp.eviction_summary}
          testid="dp-eviction"
        />)}
        {ordered.includes("criminal_report") && (
        <MetricCard
          icon={<AlertTriangle className="h-4 w-4" />}
          label="Criminal Records"
          value={booleanLabel(dp.criminal_records_found, "Clear", "Records found")}
          tone={toneForBool(dp.criminal_records_found, /*invert*/ true)}
          detail={dp.criminal_summary}
          testid="dp-criminal"
        />)}
        {dp.document_validation !== undefined && (
        <MetricCard
          icon={<FileSearch className="h-4 w-4" />}
          label="Document Validation"
          value={docLabel(dp.document_validation)}
          tone={docTone(dp.document_validation)}
          detail={dp.document_summary}
          testid="dp-document"
        />)}
        {dp.employment_verified !== undefined && (
        <MetricCard
          icon={<Briefcase className="h-4 w-4" />}
          label="Employment"
          value={verifyLabel(dp.employment_verified)}
          tone={verifyTone(dp.employment_verified)}
          detail={dp.employment_summary}
          testid="dp-employment"
        />)}
        {dp.landlord_verified !== undefined && (
        <MetricCard
          icon={<Home className="h-4 w-4" />}
          label="Landlord Reference"
          value={verifyLabel(dp.landlord_verified)}
          tone={verifyTone(dp.landlord_verified)}
          detail={dp.landlord_summary}
          testid="dp-landlord"
        />)}
        <RentToIncomeCard rti={dp.rent_to_income_ratio} income={dp.monthly_income} rent={monthlyRent} />
      </div>

      {dp.staff_notes && (
        <div className="mt-6 bg-[#F3EFEA] border-l-2 border-[#0A192F] p-4 text-sm text-[#0A192F]" data-testid="dp-staff-notes">
          <div className="font-overline text-[#4A5568] mb-1">Internal notes · NTN only</div>
          {dp.staff_notes}
        </div>
      )}

      {dp.updated_by_name && (
        <div className="mt-5 text-[10px] font-mono tracking-[0.12em] text-[#4A5568]">
          ENTERED BY {dp.updated_by_name?.toUpperCase()} · {fmtDate(dp.updated_at)}
          {dp.staff_verdict_override && dp.suggested_verdict && dp.staff_verdict_override !== dp.suggested_verdict && (
            <span className="ml-2">· STAFF OVERRODE AUTO-SUGGESTION ({dp.suggested_verdict?.toUpperCase()})</span>
          )}
        </div>
      )}
    </section>
  );
}

/* ---------------- Verdict pill ---------------- */
function VerdictPill({ verdict, overridden }) {
  const cfg = {
    approve: { bg: "bg-[#22C55E]", ring: "ring-[#22C55E]/40", label: "APPROVE", Icon: CheckCircle2 },
    conditional: { bg: "bg-[#F59E0B]", ring: "ring-[#F59E0B]/40", label: "CONDITIONAL", Icon: AlertTriangle },
    deny: { bg: "bg-[#EF4444]", ring: "ring-[#EF4444]/40", label: "DENY", Icon: XCircle },
  }[verdict] || { bg: "bg-[#4A5568]", ring: "ring-[#4A5568]/40", label: verdict?.toUpperCase() || "—", Icon: MinusCircle };
  return (
    <div className="flex flex-col items-end" data-testid={`dp-verdict-${verdict}`}>
      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm text-white text-xs font-mono font-bold tracking-[0.16em] ${cfg.bg} ring-2 ring-inset ${cfg.ring}`}>
        <cfg.Icon className="h-4 w-4" strokeWidth={2.5} />
        {cfg.label}
      </div>
      <div className="text-[9px] font-mono tracking-[0.18em] text-[#4A5568] mt-1.5">
        {overridden ? "STAFF OVERRIDE" : "AUTO-SUGGESTED"}
      </div>
    </div>
  );
}

/* ---------------- DP Score arc gauge ---------------- */
function ScoreGauge({ label, value, max = 100, naLabel = null }) {
  const has = value != null;
  const pct = Math.max(0, Math.min(100, (Number(value || 0) / max) * 100));
  const radius = 78;
  const stroke = 14;
  const C = Math.PI * radius; // half-circumference (semi-circle)
  const offset = C - (pct / 100) * C;
  const tone = pct >= 75 ? "#22C55E" : pct >= 50 ? "#F59E0B" : "#EF4444";
  const band = pct >= 75 ? "Strong" : pct >= 50 ? "Moderate" : pct >= 1 ? "Elevated risk" : "Not entered";

  return (
    <div className="bg-[#F3EFEA]/40 rounded-sm p-6 flex flex-col items-center" data-testid="dp-score-gauge">
      <div className="font-overline text-[#4A5568]">{label}</div>
      <svg viewBox="0 0 200 110" className="w-full max-w-[280px] mt-2">
        <path d={`M 12 100 A ${radius} ${radius} 0 0 1 188 100`}
          fill="none" stroke="#E5E0D8" strokeWidth={stroke} strokeLinecap="round" />
        {has && (
          <path d={`M 12 100 A ${radius} ${radius} 0 0 1 188 100`}
            fill="none"
            stroke={tone}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={C}
            strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 1s ease-out" }}
          />
        )}
        <text x="100" y="82" textAnchor="middle" fontFamily="Georgia, serif"
              fontSize="42" fill="#0A192F" fontWeight="600">
          {has ? Math.round(value) : (naLabel || "—")}
        </text>
        <text x="100" y="100" textAnchor="middle" fontFamily="Helvetica, sans-serif"
              fontSize="9" letterSpacing="3" fill="#4A5568" fontWeight="700">
          / {max}
        </text>
      </svg>
      <div className={`text-xs font-mono tracking-[0.14em] mt-1`} style={{ color: tone }}>
        {has ? band.toUpperCase() : (naLabel ? "NOT AVAILABLE" : "AWAITING DATA")}
      </div>
    </div>
  );
}

/* ---------------- FICO bar ---------------- */
function FicoBar({ value, status }) {
  // v1.0.9 — three states: numeric score / -9999 insufficient history / N/A.
  const na = status === "not_available";
  const insuff = status === "insufficient_history" || value === -9999;
  const has = !na && !insuff && value != null;
  // FICO range 300-850
  const pct = has ? Math.max(0, Math.min(100, ((Number(value) - 300) / 550) * 100)) : 0;
  const tier = na ? "No score available"
    : insuff ? "Insufficient credit history"
    : !has ? "Awaiting data"
    : value >= 800 ? "Exceptional"
    : value >= 740 ? "Very Good"
    : value >= 670 ? "Good"
    : value >= 580 ? "Fair"
    : "Poor";
  const tone = !has ? "#4A5568"
    : value >= 740 ? "#22C55E"
    : value >= 670 ? "#84CC16"
    : value >= 580 ? "#F59E0B"
    : "#EF4444";

  return (
    <div className="bg-[#F3EFEA]/40 rounded-sm p-6 flex flex-col" data-testid="dp-fico">
      <div className="flex items-baseline justify-between">
        <div className="font-overline text-[#4A5568]">FICO Score</div>
        <div className="text-[10px] font-mono text-[#4A5568] tracking-[0.12em]">300 – 850</div>
      </div>
      <div className="flex items-baseline gap-3 mt-3">
        <div className="font-serif text-5xl text-[#0A192F]">{has ? value : na ? "N/A" : insuff ? "-9999" : "—"}</div>
        <div className="text-xs font-mono tracking-[0.14em]" style={{ color: tone }}>
          {tier.toUpperCase()}
        </div>
      </div>
      {/* Tier bar */}
      <div className="mt-6">
        <div className="relative h-2 rounded-full bg-gradient-to-r from-[#EF4444] via-[#F59E0B] to-[#22C55E]">
          {has && (
            <div className="absolute -top-1.5 h-5 w-1 bg-[#0A192F] rounded-sm shadow-sm"
                 style={{ left: `calc(${pct}% - 2px)`, transition: "left 1s ease-out" }} />
          )}
        </div>
        <div className="flex justify-between text-[9px] font-mono tracking-[0.12em] text-[#4A5568] mt-2">
          <span>POOR</span><span>FAIR</span><span>GOOD</span><span>VERY GOOD</span><span>EXCEPTIONAL</span>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Metric Card ---------------- */
function MetricCard({ icon, label, value, tone, detail, testid }) {
  const palette = {
    good: { bg: "bg-[#22C55E]/10", border: "border-[#22C55E]/25", text: "text-[#15803D]", dot: "bg-[#22C55E]" },
    warn: { bg: "bg-[#F59E0B]/10", border: "border-[#F59E0B]/25", text: "text-[#92400E]", dot: "bg-[#F59E0B]" },
    bad:  { bg: "bg-[#EF4444]/10", border: "border-[#EF4444]/25", text: "text-[#B91C1C]", dot: "bg-[#EF4444]" },
    none: { bg: "bg-[#F3EFEA]",     border: "border-[#0A192F]/10", text: "text-[#4A5568]", dot: "bg-[#4A5568]/40" },
  }[tone || "none"];
  return (
    <div className={`border ${palette.border} ${palette.bg} rounded-sm p-4`} data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className={`${palette.text}`}>{icon}</span>
        <div className="font-overline text-[#4A5568]">{label}</div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <span className={`h-2 w-2 rounded-full ${palette.dot}`} />
        <div className={`text-sm font-semibold ${palette.text}`}>{value}</div>
      </div>
      {detail && <div className="text-xs text-[#4A5568] mt-2 leading-snug">{detail}</div>}
    </div>
  );
}

/* ---------------- Rent-to-income card ---------------- */
function RentToIncomeCard({ rti, income, rent }) {
  const has = rti != null;
  const pct = has ? Math.max(0, Math.min(100, Number(rti))) : 0;
  const tone = !has ? "none" : pct <= 30 ? "good" : pct <= 40 ? "warn" : "bad";
  const palette = {
    good: { bar: "bg-[#22C55E]", text: "text-[#15803D]" },
    warn: { bar: "bg-[#F59E0B]", text: "text-[#92400E]" },
    bad:  { bar: "bg-[#EF4444]", text: "text-[#B91C1C]" },
    none: { bar: "bg-[#4A5568]/30", text: "text-[#4A5568]" },
  }[tone];
  return (
    <div className="border border-[#0A192F]/10 bg-[#F3EFEA]/40 rounded-sm p-4 sm:col-span-2 lg:col-span-1" data-testid="dp-rti">
      <div className="flex items-center gap-2">
        <TrendingUp className={`h-4 w-4 ${palette.text}`} />
        <div className="font-overline text-[#4A5568]">Rent-to-Income</div>
      </div>
      <div className="flex items-baseline gap-2 mt-3">
        <div className={`font-serif text-2xl ${palette.text}`}>{has ? `${pct.toFixed(1)}%` : "—"}</div>
        {has && (
          <div className={`text-[10px] font-mono tracking-[0.12em] ${palette.text}`}>
            {pct <= 30 ? "HEALTHY" : pct <= 40 ? "TIGHT" : "OVER-LEVERAGED"}
          </div>
        )}
      </div>
      <div className="relative h-1.5 rounded-full bg-[#0A192F]/10 mt-3 overflow-hidden">
        <div className={`absolute inset-y-0 left-0 ${palette.bar}`} style={{ width: `${pct}%`, transition: "width 1s ease-out" }} />
        {/* 30% & 40% guideline ticks */}
        <div className="absolute top-0 bottom-0 w-px bg-[#0A192F]/30" style={{ left: "30%" }} />
        <div className="absolute top-0 bottom-0 w-px bg-[#0A192F]/30" style={{ left: "40%" }} />
      </div>
      <div className="text-[10px] font-mono tracking-[0.1em] text-[#4A5568] mt-2 flex justify-between">
        <span>0%</span><span>30% • 40%</span><span>100%</span>
      </div>
      {(income || rent) && (
        <div className="text-xs text-[#4A5568] mt-2">
          {rent ? `Rent $${Number(rent).toLocaleString()}/mo` : ""}
          {rent && income ? " · " : ""}
          {income ? `Income $${Number(income).toLocaleString()}/mo` : ""}
        </div>
      )}
    </div>
  );
}

/* ---------------- Helpers ---------------- */
function booleanLabel(v, falseLabel, trueLabel) {
  if (v === true) return trueLabel;
  if (v === false) return falseLabel;
  return "Awaiting data";
}
function toneForBool(v, invert = false) {
  if (v == null) return "none";
  const isGood = invert ? v === false : v === true;
  return isGood ? "good" : "bad";
}
function verifyLabel(v) {
  // 2026-02: DecisionPoint Employment/Landlord vocabulary — "not_attempted"
  // now covers every active-but-not-completed state ("In Progress").
  return { verified: "Verified", unable: "Unverifiable", not_attempted: "In Progress" }[v] || "In Progress";
}
function verifyTone(v) {
  return { verified: "good", unable: "warn", not_attempted: "none" }[v] || "none";
}
function docLabel(v) {
  // 2026-02: 5-state DP Documents vocabulary. Legacy DB keys are aliased
  // for backward-compat rendering of screenings written before the split.
  return {
    valid:              "Valid",
    invalid:            "Invalid",
    fraud_suspected:    "Invalid",              // legacy DB alias
    unable_to_validate: "Unable to Validate",
    in_progress:        "In Progress",
    not_attempted:      "Not Attempted",
  }[v] || "Not Attempted";
}
function docTone(v) {
  return {
    valid:              "good",
    invalid:            "bad",
    fraud_suspected:    "bad",
    unable_to_validate: "warn",
    in_progress:        "none",
    not_attempted:      "none",
  }[v] || "none";
}
